import { UTM_FIELDS } from "@/shared/const/storageKeys";

export interface UtmFields {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  gclid?: string;
  gbraid?: string;
  wbraid?: string;
  [key: string]: string | undefined;
}

/**
 * Utility class for managing UTM fields in localStorage
 */
export class UtmStorage {
  private static readonly STORAGE_KEY = UTM_FIELDS;

  static get(): UtmFields | null {
    const stored = localStorage.getItem(this.STORAGE_KEY);

    if (!stored) {
      return null;
    }

    try {
      const parsed = JSON.parse(stored);
      return typeof parsed === "object" && parsed !== null ? parsed : null;
    } catch {
      return null;
    }
  }

  static set(fields: UtmFields): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(fields));
    } catch (err) {
      console.error("Failed to store UTM fields:", err);
    }
  }

  static extractFromUrl(searchParams: URLSearchParams): UtmFields {
    const fields: UtmFields = {};

    // capture common UTM parameters and Google Ads click ids
    const utmParams = [
      "utm_source",
      "utm_medium",
      "utm_campaign",
      "utm_term",
      "utm_content",
      "gclid",
      "gbraid",
      "wbraid",
    ];

    utmParams.forEach((param) => {
      const value = searchParams.get(param);
      if (value) {
        fields[param] = value;
      }
    });

    // capture any other utm_* parameters
    searchParams.forEach((value, key) => {
      const normalizedKey = key.toLowerCase();
      if (normalizedKey.startsWith("utm_")) {
        fields[normalizedKey] = value;
      }
    });

    return fields;
  }

  static merge(newFields: UtmFields): UtmFields {
    const existing = this.get() || {};
    return { ...existing, ...newFields };
  }

  static captureFromUrl(searchParams: URLSearchParams): boolean {
    const fields = this.extractFromUrl(searchParams);

    if (Object.keys(fields).length > 0) {
      // merge with existing to preserve previously captured UTM fields
      const merged = this.merge(fields);
      this.set(merged);
      return true;
    }

    return false;
  }

  static clear(): void {
    try {
      localStorage.removeItem(this.STORAGE_KEY);
    } catch (err) {
      console.error("Failed to clear UTM fields:", err);
    }
  }

  static hasFields(): boolean {
    const fields = this.get();
    return fields !== null && Object.keys(fields).length > 0;
  }
}
